import { EmptyState } from '../../shared/ui'
import type { IslandProps } from '../registry'
import { BusinessView } from './dashboard/BusinessView'
import { DashboardHeader } from './dashboard/DashboardHeader'
import { DashboardSkeleton } from './dashboard/DashboardSkeleton'
import { OverviewSection } from './dashboard/OverviewSection'
import { DASHBOARD_CSS } from './dashboard/styles'
import { useOutreachDashboard, type DashData } from './dashboard/useOutreachDashboard'

function hasPrograms(data: DashData): boolean {
  if (data.scope === 'business') return data.stats.by_program.length > 0
  return data.overview.businesses.some((b) => b.stats.by_program.length > 0)
}

interface BodyProps {
  data: DashData
  days: number
  currentId: string | null
}

function Body({ data, days, currentId }: BodyProps) {
  if (!hasPrograms(data)) {
    return <EmptyState title="No programs yet" message="Create one in Settings, Programs." />
  }
  if (data.scope === 'all') {
    return <OverviewSection overview={data.overview} currentId={currentId} days={days} />
  }
  return <BusinessView stats={data.stats} days={days} />
}

// The registry passes IslandProps; the dashboard reads its own data and needs none of them.
export default function OutreachDashboard(_props: IslandProps) {
  const { scope, setScope, days, setDays, refresh, state, canPickScope, currentId } =
    useOutreachDashboard()
  const { status, data, error } = state
  const timezone = data && data.scope === 'business' ? data.stats.timezone : null

  return (
    <div className="od-root">
      <style>{DASHBOARD_CSS}</style>
      <DashboardHeader
        scope={scope}
        onScope={setScope}
        canPickScope={canPickScope}
        days={days}
        onDays={setDays}
        onRefresh={refresh}
        loading={status === 'loading'}
        timezone={timezone}
      />
      {status === 'error' && (
        <EmptyState
          role="alert"
          title="Could not load outreach stats"
          message={error ?? 'Something went wrong.'}
          cta={{ label: 'Retry', onClick: refresh }}
        />
      )}
      {status !== 'error' && !data && <DashboardSkeleton />}
      {status !== 'error' && data && (
        <div
          className={status === 'loading' ? 'od-stack od-busy' : 'od-stack'}
          aria-busy={status === 'loading'}
        >
          <Body data={data} days={days} currentId={currentId} />
        </div>
      )}
    </div>
  )
}
